import { Eye, MapPin, Truck } from "lucide-react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { MyFortAgentItem } from "@/api/types";
import { showInfo } from "@/lib/toast";
import { getAgentTierKey, getTokenName } from "@/config/common";
import { C } from "@/components/modal/home";
import { useMyAgentsUiStore } from "@/store/useMyAgentsUiStore";
import {
  formatEtherWithDecimals,
  formatThousands,
  formatUsdPrefix,
} from "@/lib/utils";
import { CCC_AIF_ADDRESS } from "@/config/env";

/** 已激活：右侧整块（标题/权益/进度/操作），样式可独立迭代 */
export function MyAgentCardActivatedPanel({
  agent,
  onAddress,
}: {
  agent: MyFortAgentItem;
  onAddress: () => void;
}) {
  const { t } = useTranslation();
  const { openActiveDetail } = useMyAgentsUiStore();
  const tierLabel = t(getAgentTierKey(agent.name));

  return (
    <>
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="font-black text-base tracking-wide"
            style={{ color: C.text }}
          >
            {agent.name}
          </span>
          <span
            className="text-[8px] px-1.5 py-[2px] rounded font-semibold"
            style={{
              background: `${C.amber}12`,
              color: C.amber,
              border: `1px solid ${C.amber}20`,
            }}
          >
            {tierLabel}
          </span>
          <span
            className="flex items-center gap-1 px-1.5 py-[2px] rounded text-[8px] font-bold ml-auto"
            style={{
              background: "rgba(34,197,94,0.06)",
              color: C.green,
              border: "1px solid rgba(34,197,94,0.10)",
            }}
          >
            <div
              className="w-1 h-1 rounded-full animate-pulse"
              style={{ background: C.green }}
            />
            {t("homePage.myAgentCard.statusRunning")}
          </span>
        </div>
        <div className="mt-1 text-[10px]" style={{ color: C.dim }}>
          {t("homePage.myAgentCard.activatedAt", {
            date: dayjs(agent.activatedAt).format("YYYY-MM-DD"),
          })}
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        <div
          className="text-center py-1.5 rounded-lg basis-[calc(25%-0.375rem)] min-w-[58px] flex-1"
          style={{
            background: "rgba(12,16,32,0.5)",
            border: "1px solid rgba(100,140,200,0.04)",
          }}
        >
          <div className="text-[8px] mb-0.5" style={{ color: C.dim }}>
            {t("homePage.benefit.custodyBoost")}
          </div>
          <div
            className="text-xs font-bold mono-num"
            style={{ color: C.primary }}
          >
            {`${agent.aiManageAddSpeedBp / 100}%`}
          </div>
        </div>
        <div
          className="text-center py-1.5 rounded-lg basis-[calc(25%-0.375rem)] min-w-[58px] flex-1"
          style={{
            background: "rgba(12,16,32,0.5)",
            border: "1px solid rgba(100,140,200,0.04)",
          }}
        >
          <div className="text-[8px] mb-0.5" style={{ color: C.dim }}>
            {t("homePage.benefit.releaseMultiple")}
          </div>
          <div
            className="text-xs font-bold mono-num"
            style={{ color: C.green }}
          >
            {`${agent.exitMultiple / 100}x`}
          </div>
        </div>
        <div
          className="text-center py-1.5 rounded-lg basis-[calc(25%-0.375rem)] min-w-[58px] flex-1"
          style={{
            background: "rgba(12,16,32,0.5)",
            border: "1px solid rgba(100,140,200,0.04)",
          }}
        >
          <div className="text-[8px] mb-0.5" style={{ color: C.dim }}>
            {t("homePage.benefit.aifToken", {
              tokenNameAif: getTokenName(CCC_AIF_ADDRESS),
            })}
          </div>
          <div
            className="text-xs font-bold mono-num"
            style={{ color: C.amber }}
          >
            {formatUsdPrefix(
              formatThousands(formatEtherWithDecimals(agent.aifValueUsdt, 0), 0)
            )}
          </div>
        </div>
        {agent.giftGiveLevel > 0 ? (
          <div
            className="text-center py-1.5 rounded-lg basis-[calc(25%-0.375rem)] min-w-[58px] flex-1"
            style={{
              background: "rgba(12,16,32,0.5)",
              border: "1px solid rgba(100,140,200,0.04)",
            }}
          >
            <div className="text-[8px] mb-0.5" style={{ color: C.dim }}>
              {t("homePage.benefit.teamLevel")}
            </div>
            <div
              className="text-xs font-bold mono-num"
              style={{ color: C.cyan }}
            >
              {`C${agent.giftGiveLevel}`}
            </div>
          </div>
        ) : null}
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {(
          [
            {
              icon: <MapPin size={10} />,
              label: t("homePage.myAgentCard.deliveryAddress"),
              onClick: onAddress,
            },
            {
              icon: <Truck size={10} />,
              label: t("homePage.myAgentCard.deliveryStatus"),
              onClick: () =>
                showInfo(t("homePage.myAgentCard.deliveryStatusToast")),
            },
            {
              icon: <Eye size={10} />,
              label: t("homePage.myAgentCard.viewDetail"),
              onClick: () => openActiveDetail(agent.agentId),
            },
          ] as const
        ).map(item => (
          <button
            key={item.label}
            type="button"
            className="flex flex-col items-center gap-1 py-1.5 rounded-lg text-[9px] font-medium transition-all active:scale-95"
            style={{
              background: "rgba(255,255,255,0.02)",
              border: `1px solid ${C.border}`,
              color: C.muted,
            }}
            onClick={e => {
              e.stopPropagation();
              item.onClick();
            }}
          >
            {item.icon}
            {item.label}
          </button>
        ))}
      </div>
    </>
  );
}
